import React from 'react';
import { DOCUMENT_CAPABILITIES } from '../../data/referenceData';
import { useReferenceData } from '../../context/ReferenceDataContext';
import { StepProps } from '../../types/form';
function labelsFor(options: {
  value: string;
  label: string;
}[], ids: string[]): string {
  return ids.map(id => options.find(o => o.value === id)?.label || id).join(', ');
}
export function ReviewStep({
  formData,
  goToStep
}: StepProps) {
  const refs = useReferenceData();
  const Section = ({
    title,
    stepIndex,
    children
  }: {
    title: string;
    stepIndex: number;
    children: React.ReactNode;
  }) => <div className="border border-border rounded-lg p-4 mb-4 bg-background" data-id="element-176">
      <div className="flex justify-between items-center mb-3 border-b border-border pb-2" data-id="element-177">
        <h3 className="font-semibold text-foreground" data-id="element-178">{title}</h3>
        <button type="button" onClick={() => goToStep?.(stepIndex)} className="text-sm text-primary hover:underline font-medium" data-id="element-179">
          Edit
        </button>
      </div>
      <div className="space-y-2 text-sm" data-id="element-180">{children}</div>
    </div>;
  const Row = ({
    label,
    value
  }: {
    label: string;
    value: React.ReactNode;
  }) => <div className="grid grid-cols-3 gap-4" data-id="element-181">
      <div className="col-span-1 text-muted-foreground" data-id="element-182">{label}</div>
      <div className="col-span-2 text-foreground font-medium" data-id="element-183">
        {value ?? <span className="text-muted-foreground italic" data-id="element-184">None</span>}
      </div>
    </div>;
  const capLabels = formData.documentCapabilities.map(k => DOCUMENT_CAPABILITIES.find(c => c.key === k)?.label || k).join(', ');
  return <div className="space-y-6" data-id="element-185">
      <div data-id="element-186">
        <h2 className="text-lg font-semibold text-foreground" data-id="element-187">Review Request</h2>
        <p className="text-sm text-muted-foreground" data-id="element-188">
          Please review the group configuration before submitting.
        </p>
      </div>

      <Section title="Submitted By" stepIndex={0} data-id="element-189">
        <Row label="Name" value={formData.submitterName} data-id="element-190" />
        <Row label="Email" value={formData.submitterEmail} data-id="element-191" />
        <Row label="Group" value={formData.groupDescription} data-id="element-192" />
      </Section>

      <Section title="Subgroups & Users" stepIndex={1} data-id="element-193">
        <Row label="Subgroups" value={formData.needSubgroups ? 'Yes' : 'No'} data-id="element-194" />
        {formData.needSubgroups && <Row label="Levels" value={formData.subgroupLevels === 3 ? '3+ levels' : `${formData.subgroupLevels} level(s)`} data-id="element-195" />}
        <Row label="Invitation limit" value={formData.numberOfUsers === '' ? 'Not specified' : formData.numberOfUsers} data-id="element-196" />
      </Section>

      <Section title="Documents" stepIndex={2} data-id="element-197">
        <Row label="Templates" value={formData.documentAllTags ? 'All' : labelsFor(refs.documentTags, formData.documentTemplateTagIds)} data-id="element-198" />
        <Row label="Services" value={formData.documentAllServices ? 'All' : labelsFor(refs.documentServices, formData.documentServiceIds)} data-id="element-199" />
        <Row label="Capabilities" value={capLabels || 'None'} data-id="element-200" />
        <Row label="Docs/month" value={formData.docsPerMonth || 'Unlimited'} data-id="element-201" />
        <Row label="In progress" value={formData.docsInProgress || 'Unlimited'} data-id="element-202" />
        <Row label="Max pages/review" value={formData.maxPagesPerReview || 'Unlimited'} data-id="element-203" />
      </Section>

      <Section title="Legal AI Copilot" stepIndex={3} data-id="element-204">
        <Row label="Access" value={formData.hasLegalAICopilot ? 'Yes' : 'No'} data-id="element-205" />
        {formData.hasLegalAICopilot && <>
            <Row label="Provinces" value={formData.copilotAllProvinces ? 'All' : labelsFor(refs.provinces, formData.copilotProvinceIds)} data-id="element-206" />
            <Row label="Practice areas" value={formData.copilotAllPracticeAreas ? 'All' : labelsFor(refs.serviceCategories, formData.copilotPracticeAreaIds)} data-id="element-207" />
            <Row label="Monthly limit" value={formData.copilotMonthlyLimit || 'Unlimited'} data-id="element-208" />
            <Row label="Chat history" value={formData.chatHistory ? 'Yes' : 'No'} data-id="element-209" />
            <Row label="Continue conversations" value={formData.continuePreviousConversations ? 'Yes' : 'No'} data-id="element-210" />
          </>}
      </Section>

      <Section title="24/7 Legal Helpline" stepIndex={4} data-id="element-211">
        <Row label="Status" value={formData.hasHelpline ? 'On' : 'Off'} data-id="element-212" />
        {formData.hasHelpline && <>
            <Row label="Intake form access" value={formData.hasAccessToIntakeForm ? 'On' : 'Off'} data-id="element-213" />
            <Row label="Display number" value={formData.helplineMaskNumber} data-id="element-214" />
            <Row label="Actual number" value={formData.helplineRealNumber} data-id="element-215" />
            <Row label="Primary email" value={formData.helplinePrimaryEmail} data-id="element-216" />
          </>}
      </Section>

      <Section title="Marketplace & Inquiries" stepIndex={5} data-id="element-217">
        <Row label="Get a quote" value={formData.hasGetAQuote ? 'On' : 'Off'} data-id="element-218" />
        {formData.hasGetAQuote && <>
            <Row label="Lawyers" value={formData.marketplaceLawyerIds.length ? labelsFor(refs.lawyers, formData.marketplaceLawyerIds) : 'All lawyers'} data-id="element-219" />
            <Row label="Discount" value={formData.marketplaceDiscount !== '' ? `${formData.marketplaceDiscount}%` : 'None'} data-id="element-220" />
          </>}
        <Row label="Legal inquiry form" value={formData.hasLegalInquiryForm ? 'Yes' : 'No'} data-id="element-221" />
      </Section>

      <Section title="Claims & Knowledge Center" stepIndex={6} data-id="element-222">
        <Row label="Submit claims" value={formData.claimsSubmit ? 'Yes' : 'No'} data-id="element-223" />
        {formData.claimsSubmit && <>
            <Row label="Edit claims" value={formData.claimsEdit ? 'Yes' : 'No'} data-id="element-224" />
            <Row label="Change status" value={formData.claimsChangeStatus ? 'Yes' : 'No'} data-id="element-225" />
            <Row label="Claims lawyers" value={formData.claimsLawyerIds.length ? labelsFor(refs.lawyers, formData.claimsLawyerIds) : 'Platform defaults'} data-id="element-226" />
          </>}
        <Row label="Knowledge Center" value={formData.hasKnowledgeCenter ? 'Yes' : 'No'} data-id="element-227" />
        {formData.hasKnowledgeCenter && <Row label="KC categories" value={formData.knowledgeCenterCategoriesInput.trim() || 'Platform defaults'} data-id="element-228" />}
      </Section>

      <Section title="General & Support" stepIndex={7} data-id="element-229">
        <Row label="Support access" value={formData.supportAccess ? 'Yes' : 'No'} data-id="element-230" />
        {formData.supportAccess && <Row label="Support categories" value={formData.supportCategoriesInput.trim() || 'Platform defaults'} data-id="element-231" />}
        <Row label="Skip phone verification" value={formData.skipPhoneVerification ? 'Yes' : 'No'} data-id="element-232" />
      </Section>

      <Section title="Other" stepIndex={8} data-id="element-233">
        <Row label="Notes" value={formData.otherNotes} data-id="element-234" />
      </Section>
    </div>;
}